import {
  BarChart3,
  Bot,
  Database,
  Files,
  Globe2,
  Mail,
  MessageCircle,
  Users,
  Workflow,
} from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const METRICS = [
  { icon: Database, label: "Dados", before: "Isolados", after: "Integrados" },
  { icon: Workflow, label: "Fluxos", before: "Interrompidos", after: "Contínuos" },
  { icon: BarChart3, label: "Decisões", before: "Atrasadas", after: "Em tempo real" },
];

const BEFORE_NODES = [
  { icon: Files, label: "Planilhas", note: "3 versões", left: "17%", top: "23%" },
  { icon: MessageCircle, label: "WhatsApp", note: "Sem histórico", left: "75%", top: "19%" },
  { icon: Mail, label: "E-mail", note: "38 pendências", left: "21%", top: "72%" },
  { icon: Users, label: "Vendas", note: "Desatualizado", left: "76%", top: "75%" },
  { icon: Database, label: "Financeiro", note: "Aguardando", left: "48%", top: "31%" },
  { icon: Globe2, label: "Site", note: "Sem integração", left: "49%", top: "78%" },
];

const AFTER_NODES = [
  { icon: Users, label: "CRM", note: "Vendas", left: "17%", top: "23%" },
  { icon: Bot, label: "Automação", note: "Atendimento", left: "80%", top: "23%" },
  { icon: Database, label: "Dados", note: "Indicadores", left: "17%", top: "75%" },
  { icon: Globe2, label: "Web", note: "Experiência", left: "80%", top: "75%" },
];

const DiagramNode = ({ icon: Icon, label, note, left, top, connected = false }) => (
  <div
    className={`absolute z-10 w-[78px] -translate-x-1/2 -translate-y-1/2 border px-2 py-2 text-center sm:w-[112px] sm:px-3 sm:py-3 lg:w-[132px] ${connected ? "border-lime-300/55 bg-[#101917] shadow-[0_0_36px_rgba(217,224,33,.09)]" : "border-white/13 bg-[#151515]"}`}
    style={{ left, top }}
  >
    <Icon size={connected ? 17 : 15} className={`mx-auto ${connected ? "text-lime-300" : "text-white/45"}`} />
    <p className={`mt-1.5 truncate text-[8px] font-bold uppercase tracking-[.11em] sm:text-[9px] ${connected ? "text-white" : "text-white/70"}`}>{label}</p>
    <p className={`mt-1 hidden truncate text-[8px] sm:block ${connected ? "text-white/38" : "text-red-300/55"}`}>{note}</p>
  </div>
);

export const BeforeAfter = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const { scrollYProgress: entryProgress } = useScroll({
    target: ref,
    offset: ["start end", "start start"],
  });

  const entryOpacity = useTransform(entryProgress, [0, 0.58, 1], [0, 0.72, 1]);
  const entryY = useTransform(entryProgress, [0, 1], [64, 0]);
  const beforeClip = useTransform(
    scrollYProgress,
    [0.06, 0.82],
    ["inset(0 0% 0 0)", "inset(0 100% 0 0)"],
  );
  const dividerPosition = useTransform(scrollYProgress, [0.06, 0.82], ["100%", "0%"]);
  const beforeLabelOpacity = useTransform(scrollYProgress, [0.06, 0.55], [1, 0]);
  const afterLabelOpacity = useTransform(scrollYProgress, [0.3, 0.82], [0, 1]);
  const afterContentScale = useTransform(scrollYProgress, [0.06, 0.82], [0.97, 1]);
  const progressScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section ref={ref} id="transformacao" data-header-theme="dark" className="relative h-[205svh] bg-[#050606] md:h-[225svh]" data-testid="before-after-section">
      <div className="sticky top-0 flex h-[100svh] min-h-[660px] items-center overflow-hidden bg-[#050606] pt-[76px]">
        <motion.div className="site-shell" style={reduce ? undefined : { opacity: entryOpacity, y: entryY }}>
          <div className="grid gap-3 lg:grid-cols-[1fr_.44fr] lg:items-end lg:gap-16">
            <div>
              <p className="eyebrow">O ponto de virada</p>
              <h2 className="display-title mt-4 max-w-[1280px] text-[clamp(2.2rem,4.35vw,4.7rem)] leading-[1.01] text-[#f1f3ec] md:mt-5">
                Sua operação não é lenta. <span className="text-white/38">Ela está desconectada.</span>
              </h2>
            </div>
            <p className="hidden max-w-lg text-sm leading-relaxed text-white/52 lg:block lg:pb-1 lg:text-base">
              A tecnologia certa não adiciona mais uma ferramenta. Ela faz toda a empresa funcionar como um sistema.
            </p>
          </div>

          <div className="mt-6 md:mt-8">
            <div className="before-after-stage relative h-[43svh] min-h-[310px] max-h-[470px] w-full overflow-hidden border border-white/14 bg-[#07100e]" data-testid="before-after-comparison">
              <motion.div className="absolute inset-0 overflow-hidden bg-[#07100e]" style={reduce ? undefined : { scale: afterContentScale }} aria-hidden="true">
                <div className="system-map-grid absolute inset-0" />
                <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1000 420" preserveAspectRatio="none">
                  <path className="system-flow-line" d="M500 210 C390 210 310 95 170 95" />
                  <path className="system-flow-line" d="M500 210 C610 210 680 95 800 95" />
                  <path className="system-flow-line" d="M500 210 C390 210 310 315 170 315" />
                  <path className="system-flow-line" d="M500 210 C610 210 680 315 800 315" />
                </svg>
                {AFTER_NODES.map((node) => <DiagramNode key={node.label} {...node} connected />)}

                <div className="absolute left-1/2 top-1/2 z-20 flex h-[82px] w-[82px] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full bg-lime-300 text-center text-[#050606] shadow-[0_0_60px_rgba(217,224,33,.2)] sm:h-[116px] sm:w-[116px]">
                  <Workflow size={20} />
                  <p className="mt-1 text-[7px] font-black uppercase tracking-[.1em] sm:text-[9px]">Clube Digital</p>
                  <p className="hidden text-[8px] font-semibold opacity-55 sm:block">Sistema central</p>
                </div>

                <motion.div className="absolute right-4 top-4 z-30 border border-lime-300/40 bg-[#07100e]/90 px-3 py-2 text-[8px] font-bold uppercase tracking-[.18em] text-lime-300 backdrop-blur sm:right-5 sm:top-5 lg:right-7 lg:top-7" style={reduce ? undefined : { opacity: afterLabelOpacity }}>
                  Agora · Conectado
                </motion.div>
                <p className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap text-[9px] font-bold uppercase tracking-[.24em] text-white/30 sm:bottom-5">Uma operação · Uma fonte de verdade</p>
              </motion.div>

              <motion.div className="absolute inset-0 overflow-hidden bg-[#0b0b0b]" style={reduce ? { clipPath: "inset(0 50% 0 0)" } : { clipPath: beforeClip }} aria-hidden="true">
                <div className="disconnected-map-grid absolute inset-0" />
                <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1000 420" preserveAspectRatio="none">
                  <path className="broken-flow-line" d="M170 95 C245 125 290 130 350 158" />
                  <path className="broken-flow-line" d="M800 85 C720 105 690 125 635 158" />
                  <path className="broken-flow-line" d="M205 310 C285 282 310 270 375 255" />
                  <path className="broken-flow-line" d="M790 315 C710 285 675 275 620 258" />
                  <path className="broken-flow-line" d="M480 130 L500 175" />
                  <path className="broken-flow-line" d="M490 325 L500 277" />
                </svg>
                {BEFORE_NODES.map((node) => <DiagramNode key={node.label} {...node} />)}

                <span className="absolute left-[35%] top-[37%] z-20 flex h-6 w-6 items-center justify-center rounded-full border border-red-400/50 bg-[#1a0d0d] text-[11px] font-bold text-red-300">×</span>
                <span className="absolute left-[63%] top-[37%] z-20 flex h-6 w-6 items-center justify-center rounded-full border border-red-400/50 bg-[#1a0d0d] text-[11px] font-bold text-red-300">×</span>
                <span className="absolute bottom-[31%] left-[38%] z-20 flex h-6 w-6 items-center justify-center rounded-full border border-red-400/50 bg-[#1a0d0d] text-[11px] font-bold text-red-300">×</span>
                <span className="absolute bottom-[31%] left-[61%] z-20 flex h-6 w-6 items-center justify-center rounded-full border border-red-400/50 bg-[#1a0d0d] text-[11px] font-bold text-red-300">×</span>

                <motion.div className="absolute left-4 top-4 z-30 border border-white/14 bg-[#0b0b0b]/90 px-3 py-2 text-[8px] font-bold uppercase tracking-[.18em] text-white/70 backdrop-blur sm:left-5 sm:top-5 lg:left-7 lg:top-7" style={reduce ? undefined : { opacity: beforeLabelOpacity }}>
                  Antes · Fragmentado
                </motion.div>
                <p className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap text-[9px] font-bold uppercase tracking-[.24em] text-white/28 sm:bottom-5">Cada área trabalha sozinha</p>
              </motion.div>

              <motion.div className="absolute inset-y-0 z-40 w-px bg-lime-300 shadow-[0_0_30px_rgba(217,224,33,.5)]" style={reduce ? { left: "50%" } : { left: dividerPosition }} aria-hidden="true">
                <span className="absolute left-1/2 top-1/2 h-14 w-[5px] -translate-x-1/2 -translate-y-1/2 bg-lime-300" />
              </motion.div>
            </div>

            <div className="grid grid-cols-3 border-b border-white/12">
              {METRICS.map(({ icon: Icon, label, before, after }, index) => (
                <div key={label} className={`grid min-h-[82px] grid-cols-[auto_1fr] items-center gap-2 px-2 py-3 sm:gap-3 sm:px-4 md:min-h-[84px] md:px-5 ${index > 0 ? "border-l border-white/12" : ""}`}>
                  <span className="hidden h-9 w-9 items-center justify-center border border-white/12 text-lime-300 sm:flex"><Icon size={16} /></span>
                  <div className="min-w-0">
                    <p className="text-[8px] font-bold uppercase tracking-[.16em] text-white/30">{label}</p>
                    <p className="mt-1 text-[10px] sm:text-xs"><span className="hidden text-white/28 line-through lg:inline">{before}</span><span className="mx-2 hidden text-white/18 lg:inline">→</span><strong className="font-semibold text-white">{after}</strong></p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="absolute bottom-0 left-0 h-[3px] w-full bg-white/8" aria-hidden="true">
          <motion.div className="h-full origin-left bg-lime-300" style={{ scaleX: progressScale }} />
        </div>
      </div>
    </section>
  );
};
